/**
 * The portal wizard's screens. Each is presentational — the
 * orchestration (which screen, when) lives in Wizard.tsx.
 */
import { CONTACT_EMAIL, useI18n } from "@/lib/i18n";
import {
  Button,
  Card,
  ContactLine,
  MicrosoftLogo,
  ScreenIcon,
  Spinner,
} from "@/components/ui";

export type LinkProblemReason =
  | "expired"
  | "revoked"
  | "not_found"
  | "already_used";

// The wizard cards sit on the cream canvas with a subtle lift.
const CARD = "bg-surface-container-low";

// ----------------------------------------------------------------- loading

export function LoadingScreen({ message }: { message: string }) {
  return (
    <Card className={CARD}>
      <Spinner />
      <p className="mt-4 text-center text-body-sm text-on-surface-variant">
        {message}
      </p>
    </Card>
  );
}

// ----------------------------------------------------------------- landing

export function LandingScreen({ onSignIn }: { onSignIn?: () => void } = {}) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <h1 className="text-page-title text-on-surface">{t("landing.tagline")}</h1>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("landing.body")}
      </p>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("landing.inviteOnly", { email: CONTACT_EMAIL })}
      </p>
      {onSignIn && (
        <div className="mt-6">
          <Button onClick={onSignIn} fullWidth>
            <MicrosoftLogo />
            {t("landing.signIn")}
          </Button>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ sign-in

export function SignInScreen({ onSignIn }: { onSignIn: () => void }) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <h1 className="text-center text-page-title text-on-surface">
        {t("signin.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("signin.body")}
      </p>
      <div className="mt-6">
        <Button onClick={onSignIn} fullWidth>
          <MicrosoftLogo />
          {t("signin.button")}
        </Button>
      </div>
      <p className="mt-4 text-center text-label-muted text-on-surface-variant">
        {t("signin.adminNote")}
      </p>
    </Card>
  );
}

// -------------------------------------------------------------- link problem

const PROBLEM_KEY: Record<LinkProblemReason, string> = {
  expired: "expired",
  revoked: "revoked",
  not_found: "notFound",
  already_used: "alreadyUsed",
};

export function LinkProblemScreen({ reason }: { reason: LinkProblemReason }) {
  const { t } = useI18n();
  const key = PROBLEM_KEY[reason];
  return (
    <Card className={CARD}>
      <ScreenIcon tone={reason === "already_used" ? "info" : "warn"} />
      <h1 className="text-center text-section-head text-on-surface">
        {t(`problem.${key}.title`)}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t(`problem.${key}.body`)}
      </p>
      <div className="text-center">
        <ContactLine />
      </div>
    </Card>
  );
}

// ------------------------------------------------------------ email mismatch

export function EmailMismatchScreen({
  invitedEmail,
  signedInEmail,
  onSwitchAccount,
}: {
  invitedEmail: string;
  signedInEmail: string;
  onSwitchAccount: () => void;
}) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <ScreenIcon tone="warn" />
      <h1 className="text-center text-section-head text-on-surface">
        {t("mismatch.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("mismatch.body", { invited: invitedEmail, signed: signedInEmail })}
      </p>
      <div className="mt-6">
        <Button onClick={onSwitchAccount} fullWidth>
          {t("mismatch.switch")}
        </Button>
      </div>
    </Card>
  );
}

// ------------------------------------------------------- confirm details

/** One read-only label/value row in the confirmation screen's detail list. */
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 px-4 py-3">
      <dt className="text-body-sm text-on-surface-variant">{label}</dt>
      <dd className="text-right text-body-sm font-medium text-on-surface">
        {value || "—"}
      </dd>
    </div>
  );
}

export function ConfirmDetailsScreen({
  customerName,
  domain,
  signedInName,
  onConfirm,
  onFlagWrong,
}: {
  customerName: string;
  domain: string;
  signedInName: string;
  onConfirm: () => void;
  onFlagWrong: () => void;
}) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <h1 className="text-page-title text-on-surface">{t("confirm.title")}</h1>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("confirm.intro")}
      </p>
      <dl className="mt-5 divide-y divide-outline-variant rounded-lg border border-outline-variant bg-surface-container-lowest">
        <DetailRow label={t("confirm.company")} value={customerName} />
        <DetailRow label={t("confirm.tenant")} value={domain} />
        <DetailRow label={t("confirm.signedInAs")} value={signedInName} />
      </dl>
      <p className="mt-3 text-label-muted text-on-surface-variant">
        {t("confirm.tenantNote")}
      </p>
      <div className="mt-6">
        <Button onClick={onConfirm} fullWidth>
          {t("confirm.button")}
        </Button>
      </div>
      <button
        type="button"
        onClick={onFlagWrong}
        className="mt-3 w-full text-center text-label-muted text-on-surface-variant hover:text-on-surface hover:underline"
      >
        {t("confirm.wrong")}
      </button>
    </Card>
  );
}

// ---------------------------------------------------------- flag wrong

export function FlagWrongScreen() {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <ScreenIcon tone="info" />
      <h1 className="text-center text-section-head text-on-surface">
        {t("flagWrong.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("flagWrong.body")}
      </p>
      <div className="text-center">
        <ContactLine />
      </div>
    </Card>
  );
}

// ------------------------------------------------------- tenant conflict

export function TenantConflictScreen() {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <ScreenIcon tone="warn" />
      <h1 className="text-center text-section-head text-on-surface">
        {t("conflict.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("conflict.body")}
      </p>
      <div className="text-center">
        <ContactLine />
      </div>
    </Card>
  );
}

// ------------------------------------------------------------- download

/** The IT-admin sideload walkthrough — a static portal-ui page. */
const INSTALL_GUIDE_HREF = "/help/install-teams";

export function DownloadScreen({
  customerName,
  onDownload,
}: {
  customerName: string;
  onDownload: () => void;
}) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <ScreenIcon tone="ok" />
      <h1 className="text-center text-section-head text-on-surface">
        {t("download.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("download.body", { company: customerName })}
      </p>
      <div className="mt-6">
        <Button onClick={onDownload} fullWidth>
          {t("download.button")}
        </Button>
      </div>
      <p className="mt-4 text-center">
        <a
          href={INSTALL_GUIDE_HREF}
          target="_blank"
          rel="noopener"
          className="text-label-muted text-on-surface-variant hover:text-on-surface hover:underline"
        >
          {t("download.guide")}
        </a>
      </p>
    </Card>
  );
}

// -------------------------------------------------------------- all set

export function AllSetScreen({ onContinue }: { onContinue: () => void }) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <ScreenIcon tone="ok" />
      <h1 className="text-center text-section-head text-on-surface">
        {t("allset.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("allset.body")}
      </p>
      <p className="mt-4 text-center">
        <a
          href={INSTALL_GUIDE_HREF}
          target="_blank"
          rel="noopener"
          className="text-body-sm text-on-surface-variant hover:text-on-surface hover:underline"
        >
          {t("allset.guide")}
        </a>
      </p>
      <div className="mt-6">
        <Button onClick={onContinue} fullWidth>
          {t("allset.continue")}
        </Button>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------- error

export function ErrorScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <Card className={CARD}>
      <ScreenIcon tone="warn" />
      <h1 className="text-center text-section-head text-on-surface">
        {t("error.title")}
      </h1>
      <p className="mt-3 text-center text-body-sm text-on-surface-variant">
        {t("error.body")}
      </p>
      <div className="mt-6">
        <Button onClick={onRetry} fullWidth>
          {t("error.retry")}
        </Button>
      </div>
    </Card>
  );
}
